<script lang="ts">
	import PercentageChange from 'components/PercentageChange/PercentageChange.svelte';
	import ChartPreview from 'components/ChartPreview/ChartPreview.svelte';
	import type { ITableData } from '../types';
	import StarIcon from 'assets/svg/star.svg?component';
	import { onMount } from 'svelte';
	import NoIcon from 'assets/svg/noCoinIcon.svg?url';
	import isInViewport from 'utils/isInViewport';
	import getStartEndDate, { type intervalType } from 'utils/chart/getStartEndDate';
	import TableRow from 'components/Table/TableRow/TableRow.svelte';
	import TableCell from 'components/Table/TableCell/TableCell.svelte';
	import activeCurrency from 'stores/activeCurrency';
	import { throttle } from 'utils/decorators';
	import { onErrorCoinLogo } from 'utils/onErrorCoinLogo';
	import { goto } from '$app/navigation';
	import { editWatchlist } from 'utils/watchlist/editWatchlist';
	import { getChartPreviewBackup } from 'services/coin-data/chart-preview-backup';
	import { browser } from '$app/environment';
	import { getCoingeckoCoinId } from 'services/coin-data/coingecko-coin-id';
	import formatNumber from 'utils/formatNumber';
	import formatCoinPrice from 'utils/formatCoinPrice';
	import PriceTooltip from 'components/PriceTooltip/PriceTooltip.svelte';
	import { getCoinRouteData } from 'services/coin-data/coin-route-data';
	import { cmcIntervals, roundUnixTime } from 'components/Chart/chartUtils/chartSettings';
	import { getCoinmarketcapChartPreviewBackup } from 'services/coin-data/chart-preview-cmc-backup';

	export let data: ITableData;
	export let dark: boolean;
	export let filterInterval: intervalType;
	export let isInnerTableFilterChanged = false;
	export let user;
	export let list;
	export let sparkline: Array<number> | string = [];
	export let isInWatchlist = false;
	export let listName;
	export let isSparklinesLoading;
	export let isMobile: boolean;
	export let toggleModal;

	let isInUserViewport = false;
	let isLoaded = true;
	let noChartData = false;
	let tableRowRef: HTMLElement;
	const iconUrl = data.iconUrl ? data.iconUrl : NoIcon;

	// Note: Rare case when cell not loaded
	async function getChartData(interval: { start: string; end: string }) {
		if (!browser) return;
		const coingeckoCoinId = await getCoingeckoCoinId('route', data.route);

		if (data?.isCustomCoin) {
			sparkline = data?.chartData?.price?.length > 0 ? data?.chartData?.price : [];
		} else if (coingeckoCoinId) {
			sparkline = await getChartPreviewBackup(
				coingeckoCoinId,
				$activeCurrency.currency,
				interval.start,
				interval.end,
				filterInterval,
			);
		} else if (!coingeckoCoinId || sparkline?.length === 0 || sparkline?.length === 1) {
			const coinmarketcapId = await getCoinRouteData(data.id).then((data) => data.coinmarketcapId);
			if (coinmarketcapId) {
				const period = getStartEndDate(filterInterval, true) as {
					start: number;
					end: number;
				};
				sparkline = await getCoinmarketcapChartPreviewBackup(
					coinmarketcapId,
					cmcIntervals[filterInterval].timePeriod,
					cmcIntervals[filterInterval].interval,
					roundUnixTime(period.start),
					roundUnixTime(period.end),
					$activeCurrency.currency,
				);
			} else {
				sparkline = [];
			}
		}
		if (sparkline?.length === 0 || sparkline?.length === 1) {
			noChartData = true;
		}
		isLoaded = true;
	}

	function checkForCurrencyEquality() {
		if (data.id === $activeCurrency.currency.toLocaleUpperCase()) {
			sparkline = [1, 1];
		}
	}

	const gotoCoin = (e: KeyboardEvent) => {
		const link = `coins/${data.route}`;
		if (e.metaKey || e.ctrlKey) {
			e.preventDefault();
			window.open(link, '_blank');
			return;
		}
		void goto(link);
	};

	const handleClick = () => {
		user?._id ? editWatchlist(data.id, list, listName) : toggleModal();
	};

	const handleScroll = throttle(() => {
		isInUserViewport = isInViewport(tableRowRef);
	}, 100);

	onMount(() => {
		isInUserViewport = isInViewport(tableRowRef);
		if (!isInUserViewport) {
			document.addEventListener('scroll', handleScroll);
		}
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	});

	$: sparkline, checkForCurrencyEquality();
	$: if (sparkline === 'empty' && isInUserViewport && !isSparklinesLoading) {
		getChartData(getStartEndDate(filterInterval, true));
	}
	$: if (isInUserViewport) {
		isLoaded = true;
	}
	$: if (isInnerTableFilterChanged) {
		isInUserViewport = isInViewport(tableRowRef);
	}
	$: coinPrice = formatCoinPrice(formatNumber(Number(data.price), $activeCurrency.currency));
</script>

<TableRow {dark} bind:tableRowRef class="coin-list-table-row" on:click={gotoCoin}>
	{#if isLoaded}
		<TableCell {dark} class="coin-rank">
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<!-- svelte-ignore a11y-no-static-element-interactions -->
			<div class="center" on:click|stopPropagation={() => handleClick()}>
				<StarIcon
					class={`icon-star ${isInWatchlist ? 'added' : 'not-in-list'}`}
					width={18}
					height={18}
				/>
				<p class="rank-text">{data.rank ?? '-'}</p>
			</div>
		</TableCell>
		<TableCell {dark} class="coin-name">
			<a href={`/coins/${data.route}`}>
				<img
					src={`${iconUrl}?size=${isMobile ? '20x20' : '30x30'}`}
					srcset={`
						${iconUrl}?size=${isMobile ? '20x20' : '30x30'} 1x,
						${iconUrl}?size=60x60 2x,
						${iconUrl}?size=90x90 3x,
					`}
					alt={`${data.symbol} logo`}
					class="coin-icon"
					loading="lazy"
					width={`${isMobile ? '20px' : '30px'}`}
					height={`${isMobile ? '20px' : '30px'}`}
					on:error={onErrorCoinLogo}
				/>
				<div>
					<p class="coin-name-text">{data.name}</p>
					<div class="meta-wrapper">
						<p class="rank-mobile" class:dark>{data.rank ?? '-'}</p>
						<span class="symbol-mobile">{data.symbol}</span>
					</div>
				</div>
			</a>
		</TableCell>

		<TableCell {dark} class="coin-symbol">
			<span>{data.symbol}</span>
		</TableCell>
		{#key $activeCurrency}
			<TableCell {dark} class="coin-price">
				<PriceTooltip price={coinPrice} />
			</TableCell>
		{/key}
		<TableCell {dark} class="coin-change-price">
			<PercentageChange {dark} dayChange={Number(data?.change)} type="percentage" />
		</TableCell>
		<TableCell {dark} class="coin-change-volume">
			{formatNumber(Number(data.volume), $activeCurrency.currency, true)}
		</TableCell>
		<TableCell {dark} class="coin-graph">
			{#if sparkline?.length > 1 && sparkline instanceof Array}
				{#key sparkline}
					<ChartPreview
						chartData={sparkline}
						isEqual={data.id === $activeCurrency.currency.toLocaleUpperCase()}
						dayChangeSync={Number(data?.change)}
					/>
				{/key}
			{:else if noChartData}
				<p class="no-data" class:dark>No data available</p>
			{:else}
				<p class="no-data" class:dark>Loading...</p>
			{/if}
		</TableCell>
		<TableCell {dark} class="coin-market-cap">
			{data.marketCap
				? formatNumber(Number(data.marketCap), $activeCurrency.currency, true)
				: formatNumber(0, $activeCurrency.currency)}
		</TableCell>
	{/if}
</TableRow>

<style lang="scss">
	@import 'coinListTableRow';
</style>
